/* Small MP4s warm near the viewport and autoplay silently only while visible. */
(() => {
  const states = new Map();
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const phone = () => matchMedia('(max-width:767px)').matches;
  let scheduled = false;
  const setState = (s, name) => { s.box.dataset.beState = name; };
  const controls = (s) => {
    if (!s.toggle) return;
    s.toggle.hidden = !s.video.currentSrc;
    s.toggle.textContent = s.video.paused ? '▶' : 'Ⅱ';
    s.toggle.setAttribute('aria-label', s.video.paused ? s.toggle.dataset.play : s.toggle.dataset.pause);
    s.toggle.setAttribute('aria-pressed', String(!s.video.paused));
  };
  const load = (s) => {
    if (s.video.getAttribute('src')) return;
    const connection = navigator.connection;
    const constrained = connection?.saveData || /(^|-)2g$|3g/.test(connection?.effectiveType || '');
    const small = phone() || constrained || s.video.getBoundingClientRect().width < 350;
    s.video.muted = true;
    s.video.defaultMuted = true;
    s.video.preload = 'metadata';
    s.video.src = small ? s.video.dataset.beSmall : s.video.dataset.beLarge;
    s.box.dataset.beQuality = small ? '480' : '720';
    setState(s, 'warming');
    s.video.load();
  };
  const pause = (s) => {
    if (!s.video.paused) s.video.pause();
    if (s.video.getAttribute('src') && !s.blocked) setState(s, 'paused');
    controls(s);
  };
  const play = (s, manual = false) => {
    if (s.pending || (!manual && (s.blocked || s.userPaused))) return;
    load(s);
    if (!s.video.paused) return;
    s.pending = true;
    s.cover.hidden = true;
    const promise = s.video.play();
    Promise.resolve(promise).then(() => {
      if (document.hidden || (!manual && s.ratio < .15) || s.userPaused) pause(s);
    }).catch((error) => {
      if (error.name === 'AbortError' || document.hidden || s.ratio < .15) return;
      s.blocked = true;
      s.cover.hidden = false;
      setState(s, 'blocked');
    }).finally(() => { s.pending = false; controls(s); });
  };
  const reconcile = () => {
    scheduled = false;
    const visible = [...states.values()].filter(s => s.ratio >= .15 && !s.userPaused && !s.blocked && s.box.isConnected);
    visible.sort((a,b) => b.ratio - a.ratio);
    const limit = navigator.connection?.saveData ? 1 : 4;
    const active = new Set(document.hidden || reducedMotion.matches ? [] : visible.slice(0,limit));
    states.forEach(s => { if (active.has(s)) play(s); else pause(s); });
  };
  const schedule = () => { if (!scheduled) { scheduled = true; requestAnimationFrame(reconcile); } };
  const hasObserver = 'IntersectionObserver' in window;
  const near = hasObserver ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const s = states.get(entry.target);
      if (s && entry.isIntersecting && !reducedMotion.matches && !document.hidden) load(s);
    });
  }, {rootMargin:'160px 0px',threshold:0}) : null;
  const visible = hasObserver ? new IntersectionObserver(entries => {
    entries.forEach(entry => { const s = states.get(entry.target); if (s) s.ratio = entry.intersectionRatio; });
    schedule();
  }, {threshold:[0,.01,.15,.35,.6,.85,1]}) : null;
  const init = (root = document) => {
    root.querySelectorAll('[data-be-video]').forEach(box => {
      const video = box.querySelector('video');
      if (!video || states.has(video)) return;
      const cover = box.querySelector('[data-be-video-play]');
      const toggle = box.querySelector('[data-be-video-toggle]');
      const s = {video,box,cover,toggle,ratio:0,pending:false,blocked:false,userPaused:false};
      states.set(video,s);
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('webkit-playsinline','');
      cover.hidden = hasObserver && !reducedMotion.matches;
      video.addEventListener('playing', () => { s.blocked = false; cover.hidden = true; setState(s,'playing'); controls(s); });
      video.addEventListener('pause', () => { if (!s.blocked) setState(s,'paused'); controls(s); });
      video.addEventListener('error', () => { s.blocked = true; cover.hidden = false; setState(s,'error'); controls(s); });
      near?.observe(video);
      visible?.observe(video);
    });
  };
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-be-video-play],[data-be-video-toggle]');
    if (!button) return;
    const s = states.get(button.closest('[data-be-video]').querySelector('video'));
    if (!s) return;
    if (button.matches('[data-be-video-toggle]') && !s.video.paused) {
      s.userPaused = true;
      pause(s);
    } else {
      s.userPaused = false;
      s.blocked = false;
      if (s.video.error && s.video.dataset.beFallback) {
        s.video.src = s.video.dataset.beFallback;
        s.video.load();
      }
      s.video.controls = true;
      play(s,true);
    }
  });
  document.addEventListener('visibilitychange', schedule);
  addEventListener('pagehide', () => states.forEach(pause));
  addEventListener('pageshow', schedule);
  addEventListener('resize', schedule, {passive:true});
  reducedMotion.addEventListener?.('change', () => {
    states.forEach(s => { if (reducedMotion.matches && s.video.paused) s.cover.hidden = false; });
    schedule();
  });
  document.addEventListener('shopify:section:load', event => { init(event.target); schedule(); });
  init();
})();

/* Preserve the previously verified mobile purchase-area protection. */
(() => {
  const adjustChat = () => {
    if (!matchMedia('(max-width:767px)').matches) { document.documentElement.removeAttribute('data-be-buy-visible'); return; }
    const buying = [...document.querySelectorAll('.be-purchase,.hdt-product-info__item.hdt-product__buy-button')].some((element) => {
      const rect = element.getBoundingClientRect();
      return rect.height > 0 && rect.top < innerHeight - 60 && rect.bottom > 0;
    });
    document.documentElement.toggleAttribute('data-be-buy-visible', buying);
    let lift = 12;
    document.querySelectorAll('.hdt-toolbar, hdt-sticky-btn-atc').forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (getComputedStyle(element).display !== 'none' && rect.height > 0 && rect.top < innerHeight && rect.bottom > 0) {
        lift = Math.max(lift, innerHeight - rect.top + 12);
      }
    });
    document.documentElement.style.setProperty('--be-chat-lift', `${Math.ceil(lift)}px`);
  };
  const targets = document.querySelectorAll('.hdt-toolbar, hdt-sticky-btn-atc');
  if ('ResizeObserver' in window) { const observer = new ResizeObserver(adjustChat); targets.forEach(e => observer.observe(e)); }
  if ('IntersectionObserver' in window) { const observer = new IntersectionObserver(adjustChat, {threshold:[0,.01,.1,.9,1]}); targets.forEach(e => observer.observe(e)); document.querySelectorAll('.be-purchase,.hdt-product-info__item.hdt-product__buy-button').forEach(e => observer.observe(e)); }
  addEventListener('resize', adjustChat, {passive:true});
  adjustChat();
})();
